import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  ContentCard,
  ContentInfo,
  ContentSection,
  Select,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck, LucidePencil } from 'lucide-react';

import * as accountDetailsState from '../../_state/accountDetails.state';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

import { keymap } from '~/shared/keymap';

//
// ⚛️ ⚛️ ⚛️ ACCOUNT TAB TRADE ⚛️ ⚛️ ⚛️
//
export const AccountTabTrade = () => {
  const t = useTranslations();
  const toast = useToast();

  const account = useAtomValue(accountDetailsState.$selectedAccount);
  const canEditAccount = useAtomValue(accountEditingState.$canEditAccount);
  const updateAccount = useSetAtom(accountEditingState.$updateRemoteAccount);
  const {
    data: paymentTermsData,
    isFetching,
    error,
  } = useAtomValue(accountDetailsState.$paymentTermConfigs);

  const [isEditing, setIsEditing] = React.useState(false);
  const [purchasePaymentTermId, setPurchasePaymentTermId] = React.useState<
    string | undefined
  >();
  const [salesPaymentTermId, setSalesPaymentTermId] = React.useState<
    string | undefined
  >();

  const paymentTerms = paymentTermsData?.paymentTerms ?? [];
  const isSupplier = account?.relationship?.isSupplier;
  const isCustomer = account?.relationship?.isCustomer;

  const paymentTermOptions = React.useMemo(
    () =>
      paymentTerms.map((term) => ({
        key: term.id,
        label: `${term.name} (${term.code})`,
      })),
    [paymentTerms],
  );

  const handleStartEditing = React.useCallback(() => {
    setPurchasePaymentTermId(
      (account?.defaultPurchasePaymentTermId as string | undefined) ??
        undefined,
    );
    setSalesPaymentTermId(
      (account?.defaultSalesPaymentTermId as string | undefined) ?? undefined,
    );
    setIsEditing(true);
  }, [account]);

  const handleCancelEditing = React.useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSaveEditing = React.useCallback(async () => {
    if (!account) return;
    try {
      await updateAccount({
        id: account.id,
        defaultPurchasePaymentTermId: purchasePaymentTermId || null,
        defaultSalesPaymentTermId: salesPaymentTermId || null,
      } as Parameters<typeof updateAccount>[0]);
      setIsEditing(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t.accountsView.messages.error,
      );
    }
  }, [
    account,
    purchasePaymentTermId,
    salesPaymentTermId,
    updateAccount,
    toast,
    t,
  ]);

  const formatPaymentTerm = React.useCallback(
    (
      term: { name: string; code: string } | null | undefined,
    ): string | undefined => {
      if (!term) return undefined;
      return `${term.name} (${term.code})`;
    },
    [],
  );

  return (
    <div className={cn('@AccountTabTrade', 'flex h-full flex-col gap-3 p-5')}>
      {(isSupplier || isCustomer) && (
        <ContentCard>
          <ContentSection
            title={
              <div className="flex items-center justify-between">
                <div>{t.accountsView.trade.paymentTerms}</div>
                {canEditAccount && !isEditing && (
                  <Button
                    variant="link"
                    className="px-0 text-sm font-medium"
                    preventFocus
                    icon={<LucidePencil />}
                    onClick={handleStartEditing}
                  >
                    {t.accountsView.trade.edit}
                  </Button>
                )}
              </div>
            }
          >
            <AsyncBoundary loading={isFetching} error={error}>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-4">
                  {isSupplier && (
                    <Select
                      label={t.accountsView.trade.purchasePaymentTerm}
                      placeholder={t.accountsView.trade.selectPaymentTerm}
                      value={purchasePaymentTermId}
                      onChange={(value) =>
                        setPurchasePaymentTermId(value ?? undefined)
                      }
                      options={paymentTermOptions}
                    />
                  )}
                  {isCustomer && (
                    <Select
                      label={t.accountsView.trade.salesPaymentTerm}
                      placeholder={t.accountsView.trade.selectPaymentTerm}
                      value={salesPaymentTermId}
                      onChange={(value) =>
                        setSalesPaymentTermId(value ?? undefined)
                      }
                      options={paymentTermOptions}
                    />
                  )}
                  <div className="col-span-2 mt-3 flex items-end justify-end gap-2 border-t border-neutral/10 pt-5">
                    <Button
                      preventFocus
                      variant="outline"
                      color="neutral"
                      className="border-neutral/20"
                      onClick={handleCancelEditing}
                    >
                      {t.accountsView.dialog.actions.cancel}
                    </Button>
                    <Button
                      icon={<LucideCheck />}
                      color="primary"
                      variant="outline"
                      preventFocus
                      onClick={handleSaveEditing}
                      keyboardShortcut={keymap.save}
                    >
                      {t.accountsView.dialog.actions.save}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {isSupplier && (
                    <ContentInfo
                      label={t.accountsView.trade.purchasePaymentTerm}
                      value={formatPaymentTerm(
                        account?.defaultPurchasePaymentTerm as
                          | { name: string; code: string }
                          | null
                          | undefined,
                      )}
                    >
                      {(value) => value}
                    </ContentInfo>
                  )}
                  {isCustomer && (
                    <ContentInfo
                      label={t.accountsView.trade.salesPaymentTerm}
                      value={formatPaymentTerm(
                        account?.defaultSalesPaymentTerm as
                          | { name: string; code: string }
                          | null
                          | undefined,
                      )}
                    >
                      {(value) => value}
                    </ContentInfo>
                  )}
                </div>
              )}
            </AsyncBoundary>
          </ContentSection>
        </ContentCard>
      )}
    </div>
  );
};
